import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
    getSyncStatus, pushStories, pullStory, resolveConflict,
    SyncScope, SyncScopeStatus, SyncPushEvent,
} from "@/api/syncApi";
import { PUSHABLE_STATUSES } from "./vendorConfig";

export interface RowUiState { inFlight: boolean; step?: string; }
export interface PushSummary { done: number; failed: number; skipped: number; }
export interface PushProgress { completed: number; total: number; }

export const AZURE_RECONNECT_MESSAGE =
    "Azure DevOps connection expired or disconnected. Please reconnect your Azure DevOps account in Integrations, then try syncing again.";
const AUTH_DISCONNECT_PATTERN = /(401|403|unauthori[sz]ed|forbidden|not[ _-]?connected|reconnect|token|oauth|expired|authentication)/i;

function normalizeSyncErrorMessage(message: string, vendor: SyncScopeStatus["vendor"] | undefined): string {
    if (vendor !== "AZURE_DEVOPS") return message;
    if (AUTH_DISCONNECT_PATTERN.test(message)) return AZURE_RECONNECT_MESSAGE;
    return message;
}

export function useSyncEngine(scope: SyncScope, open: boolean) {
    const { getToken } = useAuth();
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;
    const [loading, setLoading] = useState(false);
    const [scopeStatus, setScopeStatus] = useState<SyncScopeStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [rowState, setRowState] = useState<Record<string, RowUiState>>({});
    const [pushing, setPushing] = useState(false);
    const [pushSummary, setPushSummary] = useState<PushSummary | null>(null);
    const [pushProgress, setPushProgress] = useState<PushProgress | null>(null);
    const statusRef = useRef<SyncScopeStatus | null>(null);
    statusRef.current = scopeStatus;

    // background=true re-fetches without flipping `loading`, so the panel's
    // list stays mounted (skeletons replace it otherwise) and Collapsible
    // group open-state / scroll position survive post-action refreshes.
    const refresh = useCallback(async (background = false) => {
        if (!background) setLoading(true);
        if (!background) setError(null);
        try {
            const token = await getTokenRef.current();
            setScopeStatus(await getSyncStatus(scope, token ?? ""));
        } catch (e) {
            const message = e instanceof Error ? e.message : "Failed to load sync status";
            setError(normalizeSyncErrorMessage(message, statusRef.current?.vendor));
        } finally {
            if (!background) setLoading(false);
        }
    }, [scope.type, scope.id]);

    useEffect(() => {
        if (open) {
            setSelected(new Set());
            setPushSummary(null);
            void refresh();
        }
    }, [open, refresh]);

    const toggle = useCallback((storyId: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(storyId) ? next.delete(storyId) : next.add(storyId);
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        setSelected(prev => {
            const pushable = (statusRef.current?.stories ?? [])
                .filter(s => PUSHABLE_STATUSES.has(s.status))
                .map(s => s.story_id);
            return prev.size === pushable.length ? new Set<string>() : new Set(pushable);
        });
    }, []);

    const clearSelection = useCallback(() => setSelected(new Set()), []);

    const applyEvent = useCallback((event: SyncPushEvent, counters: PushSummary) => {
        if (event.step === "preparing" || event.step === "pushing") {
            setRowState(prev => ({ ...prev, [event.story_id]: { inFlight: true, step: event.step } }));
            return;
        }
        setRowState(prev => ({ ...prev, [event.story_id]: { inFlight: false } }));
        setPushProgress(prev => prev && { ...prev, completed: prev.completed + 1 });
        if (event.step === "done") {
            counters.done += 1;
            setScopeStatus(prev => prev && {
                ...prev,
                stories: prev.stories.map(s => s.story_id === event.story_id
                    ? { ...s, status: "SYNCED" as const, external_key: event.external_key ?? s.external_key, external_url: event.external_url ?? s.external_url, last_error: null }
                    : s),
            });
        } else if (event.step === "error") {
            counters.failed += 1;
            if (event.error && event.error !== "needs_review") {
                const normalized = normalizeSyncErrorMessage(event.error, statusRef.current?.vendor);
                if (statusRef.current?.vendor === "AZURE_DEVOPS") {
                    setError(normalized);
                }
                setScopeStatus(prev => prev && {
                    ...prev,
                    stories: prev.stories.map(s => s.story_id === event.story_id
                        ? { ...s, last_error: normalized } : s),
                });
            }
        }
    }, []);

    const runPush = useCallback(async (candidateIds: string[]) => {
        const rows = statusRef.current?.stories ?? [];
        const byId = new Map(rows.map(r => [r.story_id, r]));
        const toSend = candidateIds.filter(id => {
            const s = byId.get(id)?.status;
            return s !== "REMOTE_CHANGED" && s !== "CONFLICT";
        });
        const counters: PushSummary = { done: 0, failed: 0, skipped: candidateIds.length - toSend.length };
        if (toSend.length === 0) { setPushSummary(counters); return; }
        setPushing(true);
        setPushSummary(null);
        setPushProgress({ completed: 0, total: toSend.length });
        try {
            const token = await getTokenRef.current();
            await pushStories(toSend, token ?? "", e => applyEvent(e, counters));
        } catch (e) {
            const message = e instanceof Error ? e.message : "Sync push failed";
            setError(normalizeSyncErrorMessage(message, statusRef.current?.vendor));
        } finally {
            // A stream failure (e.g. network drop mid-push) can leave rows the
            // server never got to reply "done"/"error" for stuck spinning
            // forever — clear every entry still marked in-flight once the
            // push settles, success or failure.
            setRowState(prev => {
                let changed = false;
                const next = { ...prev };
                for (const id of Object.keys(next)) {
                    if (next[id]?.inFlight) {
                        next[id] = { inFlight: false };
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
            setPushing(false);
            setPushProgress(null);
            setPushSummary(counters);
            void refresh(true);
        }
    }, [applyEvent, refresh]);

    const pushSelected = useCallback(() => runPush([...selected]), [runPush, selected]);
    const pushOne = useCallback((storyId: string) => runPush([storyId]), [runPush]);

    // pullOne/resolveOne report success back to the caller: the conflict review
    // modal stays open on `false` and shows its Apply-failed state instead of
    // closing as if the resolve landed.
    const pullOne = useCallback(async (storyId: string): Promise<boolean> => {
        setRowState(prev => ({ ...prev, [storyId]: { inFlight: true, step: "pulling" } }));
        try {
            const token = await getTokenRef.current();
            const result = await pullStory(storyId, token ?? "");
            // Carry the pulled content so an editor with this story open can
            // refresh in place instead of autosaving stale content back.
            window.dispatchEvent(new CustomEvent("storyContentUpdated", { detail: {
                userStoryId: storyId,
                sectionId: result?.section_id ?? null,
                descriptionHtml: result?.description_html ?? null,
            } }));
            await refresh(true);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to pull updates");
            return false;
        } finally {
            setRowState(prev => ({ ...prev, [storyId]: { inFlight: false } }));
        }
    }, [refresh]);

    const resolveOne = useCallback(async (storyId: string, resolution: "ours" | "theirs"): Promise<boolean> => {
        setRowState(prev => ({ ...prev, [storyId]: { inFlight: true, step: "resolving" } }));
        try {
            const token = await getTokenRef.current();
            const result = await resolveConflict(storyId, resolution, token ?? "");
            if (resolution === "theirs") {
                window.dispatchEvent(new CustomEvent("storyContentUpdated", { detail: {
                    userStoryId: storyId,
                    sectionId: result?.section_id ?? null,
                    descriptionHtml: result?.description_html ?? null,
                } }));
            }
            await refresh(true);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to resolve conflict");
            return false;
        } finally {
            setRowState(prev => ({ ...prev, [storyId]: { inFlight: false } }));
        }
    }, [refresh]);

    return { loading, scopeStatus, error, selected, toggle, toggleAll, clearSelection,
             rowState, pushing, pushSummary, pushProgress, refresh, pushSelected, pushOne, pullOne, resolveOne };
}
